/**
 * Stored file references, and turning them into links that open.
 *
 * order_files, document_files (this project) and keystone's order_documents
 * are private buckets. A stored value never opens by itself any more: ask
 * `storage-url` for a signed link (valid 10 minutes) and use that.
 *
 * Three shapes are in the database, all valid forever:
 *
 *   storage://dommek/order_files/order_123/3f2a…c9.pdf        canonical (new)
 *   https://<ref>.supabase.co/storage/v1/object/public/<bucket>/<path>  legacy
 *   order_123/1747852425012_Pet form.pdf                       bare path
 *
 * A bare path does not say which bucket it is in; callers that hold one pass
 * `defaultBucket`. The parser mirrors supabase/functions/_shared/storageRefs.ts
 * — keep the two in step.
 */
import { supabase } from "@/integrations/supabase/client";
import { config } from "@/config";

export type StorageProject = "dommek" | "keystone";

export interface StorageRef {
  project: StorageProject;
  bucket: string;
  /** Object name inside the bucket, decoded, with no leading slash. */
  path: string;
}

export const CANONICAL_PREFIX = "storage://";

export const PROJECT_BY_SUPABASE_REF: Record<string, StorageProject> = {
  aplrbmggxypqwcetxqdo: "dommek", // Dommek production
  davghtlzjtbjpqjabdio: "dommek", // Dommek stage
  mzcflsaccyazdyhckknm: "keystone", // Keystone (buyer portal)
};

/** Buckets that stay public: their stored URLs open as they are. */
const PUBLIC_BUCKETS: Record<StorageProject, ReadonlySet<string>> = {
  dommek: new Set(["migrated-files", "public-assets"]),
  keystone: new Set(),
};

const PROJECTS: ReadonlySet<string> = new Set(["dommek", "keystone"]);
const BUCKET_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,62}$/;
const OBJECT_URL_RE =
  /^\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/;

export interface ParseOptions {
  /** Bucket a BARE path belongs to. Without it a bare path does not parse. */
  defaultBucket?: string;
  /** Project a bare path belongs to. Defaults to dommek. */
  defaultProject?: StorageProject;
}

function safeDecode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

function cleanPath(raw: string): string | null {
  const path = raw.replace(/^\/+/, "");
  if (!path || path.length > 1024) return null;
  const segments = path.split("/");
  if (segments.some((s) => s === "" || s === "." || s === "..")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f]/.test(path)) return null;
  return path;
}

function build(project: string, bucket: string, path: string | null): StorageRef | null {
  if (!PROJECTS.has(project)) return null;
  if (!BUCKET_RE.test(bucket)) return null;
  if (path === null) return null;
  const clean = cleanPath(path);
  if (!clean) return null;
  return { project: project as StorageProject, bucket, path: clean };
}

/** { project, bucket, path } for a stored value, or null when it is not ours. */
export function parseStorageRef(value: unknown, opts: ParseOptions = {}): StorageRef | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;

  if (raw.startsWith(CANONICAL_PREFIX)) {
    const rest = raw.slice(CANONICAL_PREFIX.length).split(/[?#]/)[0];
    const [project, bucket, ...pathParts] = rest.split("/");
    if (!project || !bucket || pathParts.length === 0) return null;
    return build(project, bucket, pathParts.join("/"));
  }

  if (/^https?:\/\//i.test(raw)) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return null;
    }
    const match = url.hostname.toLowerCase().match(/^([a-z0-9]{20})\.supabase\.co$/);
    const project = match ? PROJECT_BY_SUPABASE_REF[match[1]] : undefined;
    if (!project) return null;
    const m = url.pathname.match(OBJECT_URL_RE);
    if (!m) return null;
    const bucket = safeDecode(m[1]);
    const decoded = m[2].split("/").map(safeDecode);
    if (!bucket || decoded.some((s) => s === null)) return null;
    return build(project, bucket, decoded.join("/"));
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return null;

  if (!opts.defaultBucket) return null;
  return build(opts.defaultProject ?? "dommek", opts.defaultBucket, raw.split(/[?#]/)[0]);
}

export function formatStorageRef(ref: StorageRef): string {
  return `${CANONICAL_PREFIX}${ref.project}/${ref.bucket}/${ref.path}`;
}

export function isCanonicalStorageRef(value: unknown): value is string {
  return typeof value === "string" && value.trim().startsWith(CANONICAL_PREFIX);
}

/** Lower-case extension without the dot, ignoring query string and fragment. */
export function storageFileExtension(value: unknown): string {
  if (typeof value !== "string") return "";
  const noQuery = value.split(/[?#]/)[0];
  const last = noQuery.split("/").pop() ?? "";
  const dot = last.lastIndexOf(".");
  if (dot <= 0 || dot === last.length - 1) return "";
  return last.slice(dot + 1).toLowerCase();
}

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]);

export const isImageFile = (value: unknown): boolean =>
  IMAGE_EXTENSIONS.has(storageFileExtension(value));

export const isPdfFile = (value: unknown): boolean => storageFileExtension(value) === "pdf";

/** A non-guessable object name that keeps the original extension. */
export function newObjectName(originalName: string): string {
  const ext = storageFileExtension(originalName);
  const safeExt = /^[a-z0-9]{1,10}$/.test(ext) ? `.${ext}` : "";
  return `${crypto.randomUUID()}${safeExt}`;
}

/**
 * Where a new upload goes, and what to store for it:
 *   newUploadTarget("order_files", `order_${id}`, file)
 *   → { path: "order_12/3f2a….pdf", ref: "storage://dommek/order_files/order_12/3f2a….pdf" }
 */
export function newUploadTarget(bucket: string, folder: string, file: { name: string }) {
  const path = `${folder.replace(/\/+$/, "")}/${newObjectName(file.name)}`;
  return { path, ref: formatStorageRef({ project: "dommek", bucket, path }) };
}

// ---------------------------------------------------------------------------
// Resolving to openable links

const MAX_REFS_PER_CALL = 100;
// storage-url signs for 600 s; stop reusing a link well before it lapses.
const CACHE_MS = 8 * 60 * 1000;
const cache = new Map<string, { url: string | null; until: number }>();

const isPublic = (ref: StorageRef) => PUBLIC_BUCKETS[ref.project].has(ref.bucket);
const looksLikeUrl = (v: string) => /^(https?:|data:|blob:)/i.test(v.trim());

async function signBatch(canonical: string[]): Promise<Record<string, string | null>> {
  const out: Record<string, string | null> = {};
  for (let i = 0; i < canonical.length; i += MAX_REFS_PER_CALL) {
    const chunk = canonical.slice(i, i + MAX_REFS_PER_CALL);
    try {
      const { data, error } = await supabase.functions.invoke("storage-url", {
        body: { refs: chunk },
      });
      if (error) throw error;
      const urls = (data as { urls?: Record<string, string | null> })?.urls ?? {};
      for (const ref of chunk) out[ref] = urls[ref] ?? null;
    } catch (e) {
      console.error("[storage] could not resolve file links:", (e as Error)?.message);
      for (const ref of chunk) out[ref] = null;
    }
  }
  return out;
}

// Requests made in the same tick (a page of signature images mounting at
// once) share one call instead of one call each.
const inflight = new Map<string, Promise<string | null>>();
let queued: Map<string, Array<(url: string | null) => void>> | null = null;

function requestSigned(canonical: string): Promise<string | null> {
  const existing = inflight.get(canonical);
  if (existing) return existing;
  const promise = new Promise<string | null>((resolve) => {
    if (!queued) {
      queued = new Map();
      setTimeout(async () => {
        const batch = queued!;
        queued = null;
        const signed = await signBatch([...batch.keys()]);
        for (const [ref, resolvers] of batch) {
          const url = signed[ref] ?? null;
          // Only cache answers; a failed call should be retried next time.
          if (url) cache.set(ref, { url, until: Date.now() + CACHE_MS });
          inflight.delete(ref);
          resolvers.forEach((r) => r(url));
        }
      }, 0);
    }
    queued.set(canonical, [...(queued.get(canonical) ?? []), resolve]);
  });
  inflight.set(canonical, promise);
  return promise;
}

/**
 * Openable links for a batch of stored values: `{ value: url | null }`.
 *
 * - Private-bucket files → a signed link from storage-url (cached ~8 min).
 * - Public-bucket files, and anything that is not one of our files (an
 *   external link, a data: URL) → returned as they are.
 * - A legacy public URL that storage-url cannot sign falls back to itself:
 *   it still works while the bucket is public, and gives nothing away.
 * - Values that are neither → null.
 */
export async function resolveStorageUrls(
  values: Array<string | null | undefined>,
  opts: ParseOptions = {}
): Promise<Record<string, string | null>> {
  const result: Record<string, string | null> = {};
  const pending = new Map<string, string[]>(); // canonical → original values
  const now = Date.now();

  for (const value of values) {
    if (!value || value in result) continue;
    const ref = parseStorageRef(value, opts);
    if (!ref) {
      result[value] = looksLikeUrl(value) || value.startsWith("/") ? value : null;
      continue;
    }
    if (isPublic(ref)) {
      result[value] = /^https?:/i.test(value.trim())
        ? value.trim()
        : `${supabaseStorageBase()}/storage/v1/object/public/${ref.bucket}/${encodePath(ref.path)}`;
      continue;
    }
    const canonical = formatStorageRef(ref);
    const hit = cache.get(canonical);
    if (hit && hit.until > now) {
      result[value] = hit.url ?? fallback(value);
      continue;
    }
    pending.set(canonical, [...(pending.get(canonical) ?? []), value]);
  }

  await Promise.all(
    [...pending].map(async ([canonical, originals]) => {
      const url = await requestSigned(canonical);
      for (const original of originals) result[original] = url ?? fallback(original);
    })
  );
  return result;
}

function fallback(original: string): string | null {
  return /^https?:/i.test(original.trim()) ? original.trim() : null;
}

function encodePath(path: string) {
  return path.split("/").map(encodeURIComponent).join("/");
}

function supabaseStorageBase(): string {
  return String(config.SUPABASE_URL ?? "").replace(/\/+$/, "");
}

export async function resolveStorageUrl(
  value: string | null | undefined,
  opts: ParseOptions = {}
): Promise<string | null> {
  if (!value) return null;
  const urls = await resolveStorageUrls([value], opts);
  return urls[value] ?? null;
}

/**
 * Open a stored file in a new tab. Call it straight from the click handler:
 * the tab is opened synchronously (so pop-up blockers allow it) and pointed
 * at the signed link once it arrives.
 */
export async function openStorageFile(
  value: string | null | undefined,
  opts: ParseOptions = {}
): Promise<boolean> {
  const win = window.open("", "_blank");
  if (win) win.opener = null;
  const url = await resolveStorageUrl(value, opts);
  if (!url) {
    win?.close();
    return false;
  }
  if (win) win.location.href = url;
  else window.open(url, "_blank", "noopener");
  return true;
}

/**
 * Download a stored file under a given name. The link is cross-origin, where
 * `<a download>` is ignored, so the bytes are fetched and saved from a blob;
 * if that fails the file is opened instead.
 */
export async function downloadStorageFile(
  value: string | null | undefined,
  fileName: string,
  opts: ParseOptions = {}
): Promise<boolean> {
  const url = await resolveStorageUrl(value, opts);
  if (!url) return false;
  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch {
    window.open(url, "_blank", "noopener");
  }
  return true;
}

/**
 * Point every <img> under `root` whose src is a private stored file at a
 * signed link, and wait for those images to load (or fail). For HTML built
 * from stored values and then rasterised (html2canvas PDFs).
 */
export async function resolveStorageImagesIn(root: ParentNode): Promise<void> {
  const images = Array.from(root.querySelectorAll("img")).filter((img) => {
    const ref = parseStorageRef(img.getAttribute("src"));
    return !!ref && !isPublic(ref);
  });
  if (images.length === 0) return;

  const urls = await resolveStorageUrls(images.map((img) => img.getAttribute("src") as string));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          const url = urls[img.getAttribute("src") as string];
          if (!url) {
            img.style.display = "none";
            return resolve();
          }
          img.crossOrigin = "anonymous";
          img.onload = () => resolve();
          img.onerror = () => {
            img.style.display = "none";
            resolve();
          };
          img.src = url;
        })
    )
  );
}

/**
 * Delete the object a stored value names — but only from `bucket`, and only
 * when no other attachment row still points at it (create-update-order
 * copies attachment rows onto new orders with the same file_path, so one
 * object can back several rows). Works for canonical refs, legacy public
 * URLs and bare paths. Best effort: a failure is logged, not thrown.
 */
export async function removeStorageObject(
  value: string | null | undefined,
  bucket: string,
  stillReferenced?: () => Promise<boolean>
): Promise<void> {
  const ref = parseStorageRef(value, { defaultBucket: bucket });
  if (!ref || ref.project !== "dommek" || ref.bucket !== bucket) return;
  try {
    if (stillReferenced && (await stillReferenced())) return;
    const { error } = await supabase.storage.from(bucket).remove([ref.path]);
    if (error) throw error;
    cache.delete(formatStorageRef(ref));
  } catch (e) {
    console.warn("[storage] could not remove object:", (e as Error)?.message);
  }
}
